import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'

/**
 * VoiceMemoInput — record a short audio memo via MediaRecorder API,
 * return the resulting Blob as a File to the parent. Parent uploads it via
 * the existing uploadAttachment (media_type='audio' auto-detected).
 *
 * Max 2 minutes per memo, cap at 1 memo per event to keep the UI clean.
 * Preview lets the user play back before saving.
 */

interface Props {
  memo: File | null
  onChange: (memo: File | null) => void
  maxSeconds?: number
}

export function VoiceMemoInput({ memo, onChange, maxSeconds = 120 }: Props) {
  const [recording, setRecording] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [supported, setSupported] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const startedAtRef = useRef<number>(0)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    setSupported(typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia)
  }, [])

  useEffect(() => {
    if (!memo) { setPreviewUrl(null); return }
    const url = URL.createObjectURL(memo)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [memo])

  useEffect(() => {
    if (!recording) return
    const id = window.setInterval(() => {
      const secs = Math.floor((Date.now() - startedAtRef.current) / 1000)
      setElapsed(secs)
      if (secs >= maxSeconds) stop()
    }, 250)
    return () => window.clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording])

  async function start() {
    setErr(null); setElapsed(0)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      chunksRef.current = []
      const rec = new MediaRecorder(stream, { mimeType: bestAudioMime() })
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data) }
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' })
        const ext = rec.mimeType?.includes('mp4') ? 'm4a' : rec.mimeType?.includes('ogg') ? 'ogg' : 'webm'
        const file = new File([blob], `voice-memo-${Date.now()}.${ext}`, { type: rec.mimeType || 'audio/webm' })
        onChange(file)
        streamRef.current?.getTracks().forEach((t) => t.stop())
        streamRef.current = null
      }
      recorderRef.current = rec
      startedAtRef.current = Date.now()
      rec.start()
      setRecording(true)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }

  function stop() {
    try { recorderRef.current?.stop() } catch { /* noop */ }
    setRecording(false)
  }

  if (!supported) {
    return (
      <div className="text-caption text-text-tertiary">Voice memo not supported in this browser.</div>
    )
  }

  return (
    <div>
      <div className="mb-2 text-caption font-medium text-text-secondary">Voice memo (optional)</div>

      {!memo && !recording && (
        <button
          type="button"
          onClick={start}
          className="glass-tile w-full flex items-center justify-center gap-3 py-4"
        >
          <MicGlyph />
          <span className="text-body font-medium text-text-primary">Tap to record</span>
        </button>
      )}

      {recording && (
        <motion.div
          initial={{ scale: 0.98 }} animate={{ scale: 1 }}
          className="glass-tile p-4 relative overflow-hidden bloom-coral"
        >
          <div className="relative z-10 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-3 w-3 rounded-full animate-pulse" style={{ background: '#E85E6E' }} />
              <div>
                <div className="kicker">Recording</div>
                <div className="mt-1 numeric-mono text-title font-medium">{formatSecs(elapsed)}</div>
              </div>
            </div>
            <button type="button" onClick={stop} className="pill-btn pill-btn--coral" style={{ minHeight: 44, padding: '10px 20px' }}>
              Stop
            </button>
          </div>
        </motion.div>
      )}

      {memo && previewUrl && !recording && (
        <div className="glass-tile p-3 flex items-center gap-3">
          <audio controls src={previewUrl} className="flex-1 h-9" />
          <button type="button" onClick={() => onChange(null)} className="text-caption text-text-tertiary underline shrink-0">
            Remove
          </button>
        </div>
      )}

      {err && <div className="mt-2 text-caption" style={{ color: '#D14758' }}>{err}</div>}
    </div>
  )
}

function bestAudioMime(): string {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) return c
  }
  return ''
}
function formatSecs(s: number): string {
  const m = Math.floor(s / 60), r = s % 60
  return `${m}:${String(r).padStart(2, '0')}`
}
function MicGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="3" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0014 0M12 18v3M8 21h8" />
    </svg>
  )
}
